import React, { useMemo, useState } from 'react'

import { ExpandOutlined } from '@ant-design/icons'
import { Modal } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  ColorType,
  MetricsChart,
  QueryConfig,
  SyncChartPointer,
  TimeRangeValue,
  TransformNullValue
} from 'metrics-chart'

import {
  CHART_CONFIGS,
  PERFORMANCE_CHART_CONFIGS,
  TRAFFIC_VALIDATION_CHART_CONFIGS
} from './chart_config'

dayjs.extend(utc)

interface ChartCardProps {
  title: string
  unit: string
  queries: QueryConfig[]
  timeRange: TimeRangeValue
  fetchData: (
    startTimeSec: number,
    endTimeSec: number,
    stepSec: number,
    query: string
  ) => Promise<any>
  aggregation?: AggregationData
  nullValue?: TransformNullValue
}

interface AggregationData {
  Max?: {
    ClusterA: string
    ClusterB: string
    Diff: string
  }
  Avg?: {
    ClusterA: string
    ClusterB: string
    Diff: string
  }
}

interface Point {
  Time: string
  ClusterA: number
  TimeB: string
  ClusterB: number
  ClusterAUnixTimestamp: number
  ClusterBUnixTimestamp: number
}

interface MetricChartComparisonProps {
  data?: {
    ClusterDiff?: Array<{
      Key: string
      Metrics: Array<{
        Tag: string
        Aggregation?: AggregationData
        Points: Array<Point>
      }>
    }>
  }
}

// 格式化聚合数据显示
function formatAggregationValue(
  clusterA: string,
  clusterB: string,
  diff: string
): { textBefore: string; percentText: string; percentColor?: string } {
  const clusterAVal = parseFloat(clusterA)
  const clusterBVal = parseFloat(clusterB)
  const diffVal = parseFloat(diff)

  const sign = diffVal > 0 ? '+' : ''
  const percentStr = `${sign}${diffVal}%`
  const color = diffVal > 0 ? '#ff4d4f' : diffVal < 0 ? '#52c41a' : undefined

  return {
    textBefore: `${clusterAVal} → ${clusterBVal}, `,
    percentText: `${percentStr}`,
    percentColor: color
  }
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  unit,
  queries,
  timeRange,
  fetchData,
  aggregation,
  nullValue = TransformNullValue.AS_ZERO
}) => {
  const [modalVisible, setModalVisible] = useState(false)

  // 格式化聚合数据
  const maxFormatted = useMemo(() => {
    if (!aggregation?.Max) return null
    return formatAggregationValue(
      aggregation.Max.ClusterA,
      aggregation.Max.ClusterB,
      aggregation.Max.Diff
    )
  }, [aggregation?.Max])

  const avgFormatted = useMemo(() => {
    if (!aggregation?.Avg) return null
    return formatAggregationValue(
      aggregation.Avg.ClusterA,
      aggregation.Avg.ClusterB,
      aggregation.Avg.Diff
    )
  }, [aggregation?.Avg])

  const chartElement = (
    <MetricsChart
      unit={unit}
      queries={queries}
      range={timeRange}
      nullValue={nullValue}
      fetchPromeData={({ endTimeSec, startTimeSec, stepSec, query }) => {
        return fetchData(startTimeSec, endTimeSec, stepSec, query)
      }}
      chartSetting={{
        legendPosition: 'bottom',
        legendSize: 65
      }}
    />
  )

  return (
    <>
      <div
        className='relative rounded bg-gray-50 h-full flex flex-col p-3'
        style={{ minHeight: '300px' }}
      >
        <div className='flex items-center justify-between mb-2'>
          <p className='text-base font-semibold m-0'>{title}</p>
          <button
            onClick={() => setModalVisible(true)}
            className='text-gray-500 hover:text-gray-700 p-1 cursor-pointer border-none bg-transparent'
            aria-label='Expand chart'
            type='button'
          >
            <ExpandOutlined className='text-lg' />
          </button>
        </div>
        <div className='flex-1'>{chartElement}</div>
        {(maxFormatted || avgFormatted) && (
          <div className='p-2 rounded text-sm border-t border-gray-200'>
            {maxFormatted && (
              <div className='mb-1'>
                <span className='font-medium mr-2'>Max:</span>
                <span>
                  {maxFormatted.textBefore}
                  <span style={{ color: maxFormatted.percentColor }}>
                    {maxFormatted.percentText}
                  </span>
                </span>
              </div>
            )}
            {avgFormatted && (
              <div>
                <span className='font-medium mr-2'>Avg:</span>
                <span>
                  {avgFormatted.textBefore}
                  <span style={{ color: avgFormatted.percentColor }}>
                    {avgFormatted.percentText}
                  </span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      <Modal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width='90%'
        title={title}
        destroyOnClose
      >
        <div style={{ height: '600px' }}>{chartElement}</div>
      </Modal>
    </>
  )
}

// 查找合适的 Metric，如果指定了 tag 则使用指定的 tag，否则优先使用 "All"，再否则使用第一个可用的
function findMetric(
  metrics?: Array<{
    Tag: string
    Aggregation?: AggregationData
    Points: Array<Point>
  }>,
  tag?: string
) {
  if (!metrics || metrics.length === 0) {
    return undefined
  }
  // 如果指定了 tag，优先使用指定的 tag
  if (tag) {
    const specifiedMetric = metrics.find((m) => m.Tag === tag)
    if (specifiedMetric) {
      return specifiedMetric
    }
  }
  // 优先查找 "All"
  const allMetric = metrics.find((m) => m.Tag === 'All')
  if (allMetric) {
    return allMetric
  }
  // 如果没有 "All"，使用第一个可用的
  return metrics[0]
}

// 从 Point 中获取时间戳，优先使用 ClusterAUnixTimestamp，如果没有则使用 Time 字段解析
function getPointTimestamp(point: Point): number {
  return point.ClusterAUnixTimestamp
    ? point.ClusterAUnixTimestamp
    : dayjs.utc(point.Time, 'YYYY-MM-DD HH:mm:ss').unix()
}

// 将 Points 数据转换为 Prometheus 格式
function convertPointsToPrometheusFormat(
  points: Array<Point>,
  isClusterA: boolean
): [number, number][] {
  return points.map((point) => {
    const value = isClusterA ? point.ClusterA : point.ClusterB
    const timestamp = getPointTimestamp(point)
    return [timestamp, value]
  })
}

export const MetricChartComparison: React.FC<MetricChartComparisonProps> = ({
  data
}) => {
  // 从数据中计算时间范围
  const timeRange: TimeRangeValue = useMemo(() => {
    if (!data?.ClusterDiff || data.ClusterDiff.length === 0) {
      // 默认时间范围：最近1小时
      const end = dayjs().unix()
      const start = dayjs().subtract(1, 'hour').unix()
      return [start, end + 1]
    }

    // 从第一个配置的图表数据中获取时间范围
    const firstConfig = CHART_CONFIGS[0]
    const clusterDiffItem = data.ClusterDiff.find(
      (item) => item.Key === firstConfig.key
    )
    const metric = findMetric(clusterDiffItem?.Metrics, firstConfig.tag)
    const points = metric?.Points
    if (points && points.length > 0) {
      const firstPoint = points[0]
      const lastPoint = points[points.length - 1]

      // 使用统一的时间戳转换函数
      const start = getPointTimestamp(firstPoint)
      const end = getPointTimestamp(lastPoint)

      return [start, end + 1]
    }

    // 默认时间范围
    const end = dayjs().unix()
    const start = dayjs().subtract(1, 'hour').unix()
    return [start, end + 1]
  }, [data])

  // 根据配置生成图表数据的辅助函数
  const generateChartConfigs = useMemo(
    () => (configs: typeof CHART_CONFIGS) => {
      return configs.map((config) => {
        const clusterDiffItem = data?.ClusterDiff?.find(
          (item) => item.Key === config.key
        )
        const metric = findMetric(clusterDiffItem?.Metrics, config.tag)
        const points = metric?.Points || []
        const aggregation = metric?.Aggregation

        // 生成唯一的 query 标识符，包含 key 和 tag（如果有）
        const queryKey = config.tag ? `${config.key}_${config.tag}` : config.key

        return {
          ...config,
          queries: [
            {
              name: 'ClusterA',
              promql: `${queryKey}_clusterA`,
              type: 'line' as const,
              color: ColorType.ORANGE
            },
            {
              name: 'ClusterB',
              promql: `${queryKey}_clusterB`,
              type: 'line' as const,
              color: ColorType.BLUE_3
            }
          ],
          points,
          aggregation
        }
      })
    },
    [data]
  )

  // Overview 章节的图表配置
  const overviewChartConfigs = useMemo(() => {
    return generateChartConfigs(CHART_CONFIGS)
  }, [generateChartConfigs])

  // Traffic Validation 章节的图表配置
  const trafficValidationChartConfigs = useMemo(() => {
    return generateChartConfigs(TRAFFIC_VALIDATION_CHART_CONFIGS)
  }, [generateChartConfigs])

  // Performance 章节的图表配置
  const performanceChartConfigs = useMemo(() => {
    return generateChartConfigs(PERFORMANCE_CHART_CONFIGS)
  }, [generateChartConfigs])

  // 数据获取函数
  const fetchData = useMemo(
    () =>
      (
        startTimeSec: number,
        endTimeSec: number,
        stepSec: number,
        query: string
      ) => {
        // 从 query 中提取 key 和 cluster 信息
        // query 格式: {key}_clusterA 或 {key}_clusterB
        const match = query.match(/^(.+)_(clusterA|clusterB)$/)
        if (!match) {
          return Promise.resolve({
            status: 'success',
            data: {
              resultType: 'matrix',
              result: []
            }
          })
        }

        const [, queryKey, cluster] = match
        const isClusterA = cluster === 'clusterA'

        // 从 queryKey 中提取 key 和 tag
        // queryKey 格式可能是: latency_p999 或 latency_p999_Select
        let key = queryKey
        let tag: string | undefined = undefined

        // 查找对应的配置，确定 tag（先在 CHART_CONFIGS 中查找，再在 TRAFFIC_VALIDATION_CHART_CONFIGS 中查找）
        let config = CHART_CONFIGS.find((c) => {
          if (c.tag) {
            return `${c.key}_${c.tag}` === queryKey
          }
          return c.key === queryKey
        })
        if (!config) {
          config = TRAFFIC_VALIDATION_CHART_CONFIGS.find((c) => {
            if (c.tag) {
              return `${c.key}_${c.tag}` === queryKey
            }
            return c.key === queryKey
          })
        }

        if (config) {
          key = config.key
          tag = config.tag
        } else {
          // 如果没有找到配置，尝试从 queryKey 中提取
          // 检查是否是 latency_p999_Select 这种格式
          const tagMatch = queryKey.match(
            /^(.+)_(Select|Insert|Update|Delete)$/
          )
          if (tagMatch) {
            key = tagMatch[1]
            tag = tagMatch[2]
          }
        }

        // 找到对应的 ClusterDiff 项
        const clusterDiffItem = data?.ClusterDiff?.find(
          (item) => item.Key === key
        )
        const metric = findMetric(clusterDiffItem?.Metrics, tag)
        const points = metric?.Points || []

        if (points.length === 0) {
          return Promise.resolve({
            status: 'success',
            data: {
              resultType: 'matrix',
              result: []
            }
          })
        }

        // 转换数据格式
        const values = convertPointsToPrometheusFormat(points, isClusterA)

        return Promise.resolve({
          status: 'success',
          data: {
            resultType: 'matrix',
            result: [
              {
                metric: {},
                values
              }
            ]
          }
        })
      },
    [data]
  )

  if (!data?.ClusterDiff || data.ClusterDiff.length === 0) {
    return (
      <section className='mb-6'>
        <h2 className='mb-4 text-2xl font-semibold'>Metric Chart Comparison</h2>
        <p className='text-gray-500'>No data available</p>
      </section>
    )
  }

  return (
    <SyncChartPointer>
      <section className='mb-6'>
        <h2 className='mb-4 text-2xl font-semibold'>Metric Chart Comparison</h2>

        {/* Overview 章节 */}
        <div className='mb-8'>
          <h3 className='mb-4 text-xl font-semibold'>Overview</h3>
          <div className='grid grid-cols-4 gap-1'>
            {overviewChartConfigs.map((config) => {
              const uniqueKey = config.tag
                ? `${config.key}_${config.tag}`
                : config.key
              return (
                <ChartCard
                  key={uniqueKey}
                  title={config.title}
                  unit={config.unit}
                  queries={config.queries}
                  timeRange={timeRange}
                  fetchData={fetchData}
                  aggregation={config.aggregation}
                  nullValue={config.nullValue}
                />
              )
            })}
          </div>
        </div>

        {/* Traffic Validation 章节 */}
        <div className='mb-8'>
          <h3 className='mb-4 text-xl font-semibold'>Traffic Validation</h3>
          <div className='grid grid-cols-4 gap-1'>
            {trafficValidationChartConfigs.map((config) => {
              const uniqueKey = config.tag
                ? `${config.key}_${config.tag}`
                : config.key
              return (
                <ChartCard
                  key={uniqueKey}
                  title={config.title}
                  unit={config.unit}
                  queries={config.queries}
                  timeRange={timeRange}
                  fetchData={fetchData}
                  aggregation={config.aggregation}
                  nullValue={config.nullValue}
                />
              )
            })}
          </div>
        </div>

        {/* Performance 章节 */}
        <div className='mb-8'>
          <h3 className='mb-4 text-xl font-semibold'>Detail Performance</h3>
          <div className='grid grid-cols-4 gap-1'>
            {performanceChartConfigs.map((config) => {
              const uniqueKey = config.tag
                ? `${config.key}_${config.tag}`
                : config.key
              return (
                <ChartCard
                  key={uniqueKey}
                  title={config.title}
                  unit={config.unit}
                  queries={config.queries}
                  timeRange={timeRange}
                  fetchData={fetchData}
                  aggregation={config.aggregation}
                />
              )
            })}
          </div>
        </div>
      </section>
    </SyncChartPointer>
  )
}
